package fr.frux.feature.auth

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxTextField
import fr.frux.core.designsystem.interaction.clearFocusOnTap
import fr.frux.core.designsystem.layout.SplitEntryLayout
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.auth.resources.Res
import fr.frux.feature.auth.resources.auth_browse_anonymously
import fr.frux.feature.auth.resources.auth_change_email
import fr.frux.feature.auth.resources.auth_code_label
import fr.frux.feature.auth.resources.auth_code_placeholder
import fr.frux.feature.auth.resources.auth_code_resent
import fr.frux.feature.auth.resources.auth_code_sent
import fr.frux.feature.auth.resources.auth_create_account
import fr.frux.feature.auth.resources.auth_email_label
import fr.frux.feature.auth.resources.auth_email_placeholder
import fr.frux.feature.auth.resources.auth_open_about
import fr.frux.feature.auth.resources.auth_request_code
import fr.frux.feature.auth.resources.auth_resend_code
import fr.frux.feature.auth.resources.auth_resend_code_cooldown
import fr.frux.feature.auth.resources.auth_session_expired
import fr.frux.feature.auth.resources.auth_title_code
import fr.frux.feature.auth.resources.auth_title_email
import fr.frux.feature.auth.resources.auth_title_username
import fr.frux.feature.auth.resources.auth_username_intro
import fr.frux.feature.auth.resources.auth_username_label
import fr.frux.feature.auth.resources.auth_username_placeholder
import fr.frux.feature.auth.resources.auth_validate_code
import org.jetbrains.compose.resources.stringResource

/**
 * Écran d'authentification passwordless (ADR 0012) : email → code → pseudo (si compte inexistant).
 *
 * L'erreur du [AuthState] est traduite en français et typée côté [AuthViewModel] (validation
 * locale, `INVALID_CODE`, échec réseau...) : cet écran n'a plus à deviner sa nature par
 * correspondance de chaînes, il l'affiche telle quelle sous le champ actif de l'étape en cours.
 *
 * @param onBrowseAnonymously Sortie sans compte. Cet écran peut être la racine d'une pile vide
 * (après une déconnexion ou une session expirée, cf. #48) : sans cette issue, il n'y a ni retour
 * ni navigation possible, et l'utilisateur est enfermé sur un formulaire. La carte n'exige pas
 * d'authentification — refuser de se reconnecter doit rester praticable.
 * @param contextMessage Sous-titre optionnel (#264) expliquant pourquoi le mur s'affiche, dérivé
 * de la route qui a demandé la connexion. `null` = pas de contexte particulier (feature-auth ne
 * connaît pas [fr.frux.navigation.Route] — le mapping vit côté appelant, cf. `FruxEntryProvider`).
 * @param brandVisual Visuel de marque du panneau gauche en grand écran (spec §6), fourni par
 * l'hôte : feature-auth ne connaît pas le logo, qui vit dans composeApp.
 * @param onOpenAbout Ouvre les informations légales et les réglages sans compte (#468). Cet écran
 * est le passage obligé de tout anonyme qui touche au Profil, c'est donc là que la porte doit
 * être. `null` masque l'entrée.
 */
@Composable
fun AuthScreen(
    onAuthSuccess: () -> Unit,
    onBrowseAnonymously: () -> Unit,
    contextMessage: String? = null,
    viewModel: AuthViewModel = rememberAuthViewModel(),
    brandVisual: @Composable BoxScope.() -> Unit = {},
    onOpenAbout: (() -> Unit)? = null,
) {
    val state by viewModel.state.collectAsState()

    SplitEntryLayout(visual = brandVisual) {
        Scaffold(
            containerColor = MaterialTheme.colorScheme.background,
        ) { innerPadding ->
            Box(
                modifier =
                    Modifier
                        .fillMaxSize()
                        .padding(innerPadding)
                        .imePadding()
                        // Tap hors du champ actif -> ferme le clavier (#126), sans consommer le geste.
                        .clearFocusOnTap(),
            ) {
                Column(
                    modifier =
                        Modifier
                            .fillMaxSize()
                            .verticalScroll(rememberScrollState())
                            .padding(FruxSpacing.lg),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center,
                ) {
                    Column(
                        modifier = Modifier.widthIn(max = 420.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        Text(
                            text = state.step.title(),
                            style = MaterialTheme.typography.headlineLarge,
                            color = MaterialTheme.colorScheme.onBackground,
                        )
                        if (contextMessage != null) {
                            Spacer(modifier = Modifier.height(FruxSpacing.sm))
                            Text(
                                text = contextMessage,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                        Spacer(modifier = Modifier.height(FruxSpacing.lg))

                        // Constat informatif, pas une erreur de saisie : la session a été terminée par
                        // le serveur, l'utilisateur n'y est pour rien. D'où un conteneur secondaire
                        // plutôt que `error`, réservé à ce qu'il doit corriger (#48).
                        if (state.sessionExpired) {
                            Surface(
                                modifier = Modifier.fillMaxWidth(),
                                shape = MaterialTheme.shapes.medium,
                                color = MaterialTheme.colorScheme.secondaryContainer,
                            ) {
                                Text(
                                    text = stringResource(Res.string.auth_session_expired),
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSecondaryContainer,
                                    modifier = Modifier.padding(FruxSpacing.md),
                                )
                            }
                            Spacer(modifier = Modifier.height(FruxSpacing.md))
                        }

                        when (state.step) {
                            AuthStep.EnterEmail -> EmailStep(state, viewModel)
                            AuthStep.EnterCode -> CodeStep(state, viewModel, onAuthSuccess)
                            AuthStep.EnterUsername -> UsernameStep(state, viewModel, onAuthSuccess)
                        }

                        Spacer(modifier = Modifier.height(FruxSpacing.md))

                        // Discrète, sous les actions du parcours : c'est une porte de sortie, pas une
                        // proposition concurrente de la connexion.
                        FruxButton(
                            text = stringResource(Res.string.auth_browse_anonymously),
                            onClick = onBrowseAnonymously,
                            style = FruxButtonStyle.Text,
                            enabled = !state.isLoading,
                        )

                        if (onOpenAbout != null) {
                            FruxButton(
                                text = stringResource(Res.string.auth_open_about),
                                onClick = onOpenAbout,
                                style = FruxButtonStyle.Text,
                                enabled = !state.isLoading,
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EmailStep(
    state: AuthState,
    viewModel: AuthViewModel,
) {
    FruxTextField(
        value = state.email,
        onValueChange = viewModel::onEmailChange,
        label = stringResource(Res.string.auth_email_label),
        placeholder = stringResource(Res.string.auth_email_placeholder),
        isError = state.error != null,
        supportingText = state.error?.userMessage(),
        enabled = !state.isLoading,
        keyboardOptions =
            KeyboardOptions(
                keyboardType = KeyboardType.Email,
                imeAction = ImeAction.Done,
            ),
        keyboardActions = KeyboardActions(onDone = { viewModel.requestCode() }),
        modifier = Modifier.fillMaxWidth(),
    )
    Spacer(modifier = Modifier.height(FruxSpacing.lg))

    FruxButton(
        text = stringResource(Res.string.auth_request_code),
        onClick = viewModel::requestCode,
        enabled = !state.isLoading,
        loading = state.isLoading,
        modifier = Modifier.fillMaxWidth(),
    )
    Spacer(modifier = Modifier.height(FruxSpacing.md))

    LegalNotice(modifier = Modifier.fillMaxWidth())
}

@Composable
private fun CodeStep(
    state: AuthState,
    viewModel: AuthViewModel,
    onAuthSuccess: () -> Unit,
) {
    Text(
        text =
            if (state.codeResent) {
                stringResource(Res.string.auth_code_resent, state.email)
            } else {
                stringResource(Res.string.auth_code_sent, state.email)
            },
        style = MaterialTheme.typography.bodyMedium,
        color =
            if (state.codeResent) {
                MaterialTheme.colorScheme.primary
            } else {
                MaterialTheme.colorScheme.onSurfaceVariant
            },
    )
    Spacer(modifier = Modifier.height(FruxSpacing.md))

    FruxTextField(
        value = state.code,
        onValueChange = viewModel::onCodeChange,
        label = stringResource(Res.string.auth_code_label),
        placeholder = stringResource(Res.string.auth_code_placeholder),
        isError = state.error != null,
        supportingText = state.error?.userMessage(),
        enabled = !state.isLoading,
        keyboardOptions =
            KeyboardOptions(
                keyboardType = KeyboardType.NumberPassword,
                imeAction = ImeAction.Done,
            ),
        keyboardActions = KeyboardActions(onDone = { viewModel.verifyCode(onAuthSuccess) }),
        modifier = Modifier.fillMaxWidth(),
    )
    Spacer(modifier = Modifier.height(FruxSpacing.sm))

    CodeNotReceivedLink(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(horizontal = FruxSpacing.xs),
    )
    Spacer(modifier = Modifier.height(FruxSpacing.lg))

    FruxButton(
        text = stringResource(Res.string.auth_validate_code),
        onClick = { viewModel.verifyCode(onAuthSuccess) },
        enabled = !state.isLoading,
        loading = state.isLoading,
        modifier = Modifier.fillMaxWidth(),
    )
    Spacer(modifier = Modifier.height(FruxSpacing.md))

    FruxButton(
        text =
            if (state.resendCooldownSeconds > 0) {
                stringResource(Res.string.auth_resend_code_cooldown, state.resendCooldownSeconds)
            } else {
                stringResource(Res.string.auth_resend_code)
            },
        onClick = viewModel::requestCode,
        style = FruxButtonStyle.Text,
        enabled = !state.isLoading && state.resendCooldownSeconds == 0,
    )
    FruxButton(
        text = stringResource(Res.string.auth_change_email),
        onClick = viewModel::backToEmail,
        style = FruxButtonStyle.Text,
        enabled = !state.isLoading,
    )
}

@Composable
private fun UsernameStep(
    state: AuthState,
    viewModel: AuthViewModel,
    onAuthSuccess: () -> Unit,
) {
    Text(
        text = stringResource(Res.string.auth_username_intro, state.email),
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
    Spacer(modifier = Modifier.height(FruxSpacing.md))

    FruxTextField(
        value = state.username,
        onValueChange = viewModel::onUsernameChange,
        label = stringResource(Res.string.auth_username_label),
        placeholder = stringResource(Res.string.auth_username_placeholder),
        isError = state.error != null,
        supportingText = state.error?.userMessage(),
        enabled = !state.isLoading,
        keyboardOptions =
            KeyboardOptions(
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Done,
            ),
        keyboardActions = KeyboardActions(onDone = { viewModel.verifyCode(onAuthSuccess) }),
        modifier = Modifier.fillMaxWidth(),
    )
    Spacer(modifier = Modifier.height(FruxSpacing.lg))

    FruxButton(
        text = stringResource(Res.string.auth_create_account),
        onClick = { viewModel.verifyCode(onAuthSuccess) },
        enabled = !state.isLoading,
        loading = state.isLoading,
        modifier = Modifier.fillMaxWidth(),
    )
    Spacer(modifier = Modifier.height(FruxSpacing.md))

    FruxButton(
        text = stringResource(Res.string.auth_change_email),
        onClick = viewModel::backToEmail,
        style = FruxButtonStyle.Text,
        enabled = !state.isLoading,
    )
}

@Composable
private fun AuthStep.title(): String =
    when (this) {
        AuthStep.EnterEmail -> stringResource(Res.string.auth_title_email)
        AuthStep.EnterCode -> stringResource(Res.string.auth_title_code)
        AuthStep.EnterUsername -> stringResource(Res.string.auth_title_username)
    }
